import { useEffect, useMemo, useState } from 'react';
import { useHistory } from 'react-router-dom';

import { ReservationListFilter } from 'api/controllers/reservations';
import {
    getRestaurantWallClockString,
    isReservableTable,
    isRequestOverdue,
    normalizeReservationSearch,
} from 'helpers/reservationHelper';
import { useThunkDispatch } from 'hooks/useThunkDispatch';
import { useAppSelector } from 'index';
import ingredientsStore from 'store/ingredientsStore';
import reservationsStore from 'store/reservationsStore';
import tableMapsStore from 'store/tableMapsStore';
import { TableMapDetails } from 'store/tableMapsStore/reducer';

export interface ReservableTable {
    id: string;
    label: string;
    seats: number;
    areaName: string;
}

export const RESERVATIONS_PATH = '/reservations';
export const SEARCH_DEBOUNCE_MS = 400;

/** Search and date fields of a Reservations tab, as typed/picked by the user. */
export interface ReservationsTabFilter {
    searchText: string;
    day: string | null;
}

export const EMPTY_TAB_FILTER: ReservationsTabFilter = { searchText: '', day: null };

/**
 * The filter to load the list with: the search applies after a short typing delay and only from 2 characters;
 * clearing it or the date applies immediately.
 */
export function useAppliedReservationFilter(filter: ReservationsTabFilter): {
    applied: ReservationListFilter;
    isDebouncing: boolean;
} {
    const search = normalizeReservationSearch(filter.searchText);
    const [debounced, setDebounced] = useState(search);

    useEffect(() => {
        if (!search) {
            setDebounced('');
            return;
        }
        const timer = setTimeout(() => setDebounced(search), SEARCH_DEBOUNCE_MS);
        return () => clearTimeout(timer);
    }, [search]);

    const applied = useMemo(
        () => ({ search: debounced, day: filter.day }),
        [debounced, filter.day]
    );
    return { applied, isDebouncing: debounced !== search };
}

export function useReservationsRestaurantId(): string | null {
    return useAppSelector((state) => {
        const information = state.restaurantDetails.restaurantInformation;
        return information.id === state.user.currentRestaurantId &&
            information.reservationModule?.isEnabled
            ? information.id
            : null;
    });
}

export const OVERDUE_CHECK_INTERVAL_MS = 15000;

let reservationClock = new Date(Date.now());
let reservationClockTimer: number | null = null;
const reservationClockSubscribers = new Set<(now: Date) => void>();

function refreshReservationClock() {
    reservationClock = new Date(Date.now());
    reservationClockSubscribers.forEach((subscriber) => subscriber(reservationClock));
}

function subscribeToReservationClock(subscriber: (now: Date) => void): () => void {
    reservationClockSubscribers.add(subscriber);
    if (reservationClockTimer === null) {
        reservationClock = new Date(Date.now());
        reservationClockTimer = window.setInterval(
            refreshReservationClock,
            OVERDUE_CHECK_INTERVAL_MS
        );
        window.addEventListener('focus', refreshReservationClock);
        document.addEventListener('visibilitychange', refreshReservationClock);
    }
    subscriber(reservationClock);

    return () => {
        reservationClockSubscribers.delete(subscriber);
        if (reservationClockSubscribers.size === 0 && reservationClockTimer !== null) {
            window.clearInterval(reservationClockTimer);
            reservationClockTimer = null;
            window.removeEventListener('focus', refreshReservationClock);
            document.removeEventListener('visibilitychange', refreshReservationClock);
        }
    };
}

/** Restaurant wall-clock time that refreshes on its own so overdue state updates without a reload. */
export function useRestaurantWallClock(): string {
    const timeZone = useAppSelector(
        (state) => state.restaurantDetails.restaurantInformation.timeZone
    );
    const [now, setNow] = useState(reservationClock);

    useEffect(() => {
        return subscribeToReservationClock(setNow);
    }, []);

    return getRestaurantWallClockString(timeZone, now);
}

export function useOpenRequestsCount(): number {
    const requests = useAppSelector((state) => state.reservations.requests);
    const wallClock = useRestaurantWallClock();
    return useMemo(
        () => requests.filter((request) => !isRequestOverdue(request, wallClock)).length,
        [requests, wallClock]
    );
}

export function useReservationRequestsLoad(onFailed?: () => void) {
    const dispatch = useThunkDispatch();
    const restaurantId = useReservationsRestaurantId();

    useEffect(() => {
        if (!restaurantId) {
            return;
        }
        dispatch(reservationsStore.actionCreators.ensureRequests(restaurantId)).then((isLoaded) => {
            if (!isLoaded) {
                onFailed?.();
            }
        });
    }, [restaurantId]); // eslint-disable-line react-hooks/exhaustive-deps
}

export function useReservationsSubpageGuard(): boolean {
    const history = useHistory();
    const isModuleEnabled = useAppSelector(
        (state) => state.restaurantDetails.restaurantInformation?.reservationModule?.isEnabled
    );
    useReservationRequestsLoad(() => history.replace(RESERVATIONS_PATH));

    return isModuleEnabled === false;
}

export function useReservationMaps(): { maps: TableMapDetails[]; tables: ReservableTable[] } {
    const dispatch = useThunkDispatch();
    const list = useAppSelector((state) => state.tableMaps.list);
    const details = useAppSelector((state) => state.tableMaps.details);
    const publishedIds = list.filter((item) => item.status !== 'Draft').map((item) => item.id);
    const publishedKey = publishedIds.join(',');

    useEffect(() => {
        dispatch(tableMapsStore.actionCreators.getTableMaps());
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    useEffect(() => {
        publishedIds
            .filter((id) => !details[id])
            .forEach((id) => dispatch(tableMapsStore.actionCreators.getTableMapDetails(id)));
    }, [publishedKey]); // eslint-disable-line react-hooks/exhaustive-deps

    return useMemo(() => {
        const maps = publishedIds.map((id) => details[id]).filter((map) => !!map);
        const tables = maps.reduce<ReservableTable[]>(
            (all, map) =>
                all.concat(
                    map.tables.filter(isReservableTable).map((table) => ({
                        id: table.id,
                        label: table.label,
                        seats: table.seats,
                        areaName: map.name,
                    }))
                ),
            []
        );
        return { maps, tables };
    }, [publishedKey, details]); // eslint-disable-line react-hooks/exhaustive-deps
}

export function useAllergens() {
    const dispatch = useThunkDispatch();
    const allergens = useAppSelector((state) => state.ingredients.allergies);
    const language = useAppSelector((state) => state.global.language);

    useEffect(() => {
        if (allergens.length === 0) {
            dispatch(ingredientsStore.actionCreators.getAllergies(language));
        }
    }, [language]); // eslint-disable-line react-hooks/exhaustive-deps

    return allergens;
}

export { useThunkDispatch };
